import Page from '/components/templates/Page'
import Welcome from '/components/pages/single-property/Welcome'
import Bonuses from '/components/pages/single-property/Bonuses'
import Introduction from '/components/pages/single-property/Introduction'
import Gallery from '/components/pages/single-property/Gallery'
import Details from '/components/pages/single-property/Details'
import Availability from '/components/pages/single-property/Availability'
import { useState, useEffect } from 'react'
import axios from '/components/configs/axios'
import moment from 'moment'
import isEmpty from 'lodash-es/isEmpty'
import capitalize from 'lodash-es/capitalize'
import styles from './styles.module.scss'
import Card from '/components/pages/single-property/components/Card'
import { withModifiers } from '/components/helpers/withModifiers'
import Map from '/components/pages/single-property/Map'
import { useSearchContext } from '/providers/SearchContext'
import { useResponsive } from '../../providers/ResponsiveContext'
import iterateArray from '../../components/helpers/iterateArray'
import FourOhFour from '../404'
const Reviews = dynamic(() =>
  import('/components/pages/single-property/Reviews')
)
const Amenities = dynamic(() =>
  import('/components/pages/single-property/Amenities')
)
const Contact = dynamic(() => import('/components/pages/common/Contact'))
const Rentals = dynamic(() => import('/components/pages/common/Rentals'))
import { getS3Url } from '/components/helpers/getS3Url'
import Updates from '../../components/pages/single-property/Updates'
import { useAuthContext } from '/providers/authProvider'
import { useRouter } from 'next/router'
import LoadingPage from '../../components/templates/LoadingPage'
import headerTranslation from '/components/general/Header/headerTranslation'
import footerTranslation from '/components/general/Footer/footerTranslations'
import contactTranslation from '/components/pages/common/Contact/contactTranslations'
import singlePropertyTranslations from '/components/pages/single-property/singlePropertyTranslation'
import ctaTranslation from '/components/pages/home/CTA/ctaTranslations'
import cache from 'memory-cache'
import dynamic from 'next/dynamic'

const Property = ({
  property,
  notFound,
  headerTranslation,
  footerTranslation,
  contactTranslation,
  pageTranslations,
  ctaTranslation,
  recommendedProperties,
  filteredDates,
  initialMinNight,
  bedrooms,
  bathrooms
}) => {
  const [blockedDates, setBlockedDates] = useState([])

  useEffect(() => {
    if (filteredDates && filteredDates.length > 0) {
      const convertedDates = filteredDates.map((dateStr) => moment.utc(dateStr))
      setBlockedDates(convertedDates)
    }
  }, [filteredDates])

  const router = useRouter()
  const { isTablet } = useResponsive()

  const [minNights, setMinNights] = useState(initialMinNight)
  const { search } = useSearchContext()
  const { isAuthorized } = useAuthContext()

  const loadingTitle = {
    en: 'Loading',
    fr: 'Chargement',
    es: 'Cargando',
    nl: 'Bezig met laden'
  }

  let bedroomOptions =
    property?.bedroom_option &&
    property?.bedroom_option?.map(
      (bedroom) => bedroom.room_title?.split(' ')[0]
    )

  let propertyConfiguration = bedroomOptions && iterateArray(bedroomOptions)

  const returnHeaderWallpaper = (property) => {
    if (property.cloudflare_image) {
      return getS3Url(property.cloudflare_image, '1980')
    }
    if (property.cloudflare_gallery && !isEmpty(property.cloudflare_gallery)) {
      return getS3Url(property.cloudflare_gallery[0])
    }
    if (property.image) {
      return property.image
    }
    if (property.pictures?.[0]?.original) {
      return property.pictures[0].original
    }
    return null // Explicitly return null if no image is found
  }

  const findMinimumNights = (searchDay) =>
    axios
      // eslint-disable-next-line no-undef
      .get(process.env.NEXT_PUBLIC_API_URL + 'listings/min-nights', {
        params: {
          listingId: property?.resource_id,
          startDate: searchDay.format('YYYY-MM-DD')
        }
      })
      .then((res) => {
        setMinNights(res.data)
      })
      .catch((error) => {
        if (error) {
          console.error(error)
        }
      })

  useEffect(() => {
    if (property && router.locale === moment.locale()) {
      search.startDate && findMinimumNights(search.startDate)
    }
  }, [search.startDate])

  if (router.isFallback) {
    return <LoadingPage title={loadingTitle[router.locale]} />
  }

  if (notFound) {
    return (
      <FourOhFour
        headerTranslation={headerTranslation}
        footerTranslation={footerTranslation}
        ctaTranslation={ctaTranslation}
      />
    )
  }

  if (property) {
    return (
      <Page
        ctaTranslation={ctaTranslation}
        headerTranslation={headerTranslation}
        footerTranslation={footerTranslation}
        title={`${property?.propertyTitle} ${property?.bedrooms} ${
          property?.bedrooms === 1 ? 'Bedroom' : 'Bedrooms'
        } ${property?.propertyType}`}
        meta={{
          title: `${property?.propertyTitle} ${property?.bedrooms} ${
            property?.bedrooms === 1 ? 'Bedroom' : 'Bedrooms'
          } ${property?.propertyType}`,
          description:
            property?.welcome_description || property?.propertyDescription,
          image: property.cloudflare_image
            ? getS3Url(property.cloudflare_image, '1980')
            : !isEmpty(property.cloudflare_gallery)
            ? getS3Url(property.cloudflare_gallery[0])
            : property?.pictures
        }}
        footer={false}
      >
        <Welcome
          blockedDates={blockedDates}
          property={property}
          minNights={minNights}
          translations={pageTranslations}
          video={!isEmpty(property.video) && property.video[0]}
          cloudflare={
            property?.cloudflare_header_video ||
            (!isEmpty(property.cloudflare_additional_video) &&
              property.cloudflare_additional_video)
          }
          background={returnHeaderWallpaper(property)}
          configuration={`${property?.prefix_welcome_label} ${propertyConfiguration}
                      ${property?.postfix_welcome_label}`}
          title={
            <>
              {property?.propertyTitle
                ? property?.propertyTitle
                : property?.nickname
                    ?.replace(/\w+/g, capitalize)
                    ?.replaceAll('-', ' ')}
              <br />
              {property.bedrooms} Bedroom
              {`${property.exclusive === 1 ? 'Exclusive' : ''} ${
                property?.propertyType?.toLowerCase() === 'villa'
                  ? 'Villa'
                  : 'Condo'
              }`}
            </>
          }
          sold={!property.is_active}
          description={
            property?.welcome_description || property?.propertyDescription
          }
        />

        <Introduction
          translations={pageTranslations.introduction}
          description={property?.propertyDescription}
          title={
            <>
              {property?.propertyTitle}
              {' - '}
              {property?.bedrooms} {'Bedroom '}
              {capitalize(property?.propertyType)}
            </>
          }
          location={
            <>
              {property?.city}, {property?.region_side}
            </>
          }
          contact={property?.introduction_person}
        />

        {isAuthorized && (
          <Updates
            title={`${property?.propertyTitle} ${
              pageTranslations.updates.documents[router.locale]
            }`}
            entries={property.updates}
          />
        )}

        <Gallery
          translations={pageTranslations.gallery}
          key={property.id}
          property={property}
          background={
            !isAuthorized
              ? 'white'
              : property.updates && isEmpty(property.updates)
              ? 'white'
              : ''
          }
        />

        <div className={styles.grid}>
          {!isTablet && (
            <div className={withModifiers('grid__item', ['fixed'], styles)}>
              <div className={styles.grid__inner}>
                <Card
                  translations={pageTranslations.welcome.card}
                  blockedDates={blockedDates}
                  property={property}
                  minNights={minNights}
                />
              </div>
            </div>
          )}

          <div className={withModifiers('grid__item', ['first'], styles)}>
            <Details
              translations={pageTranslations.details}
              bedrooms={bedrooms}
              bathrooms={bathrooms}
              common_areas={property.common_areas_description}
              bathroom_description={property.bathroom_description}
            />
          </div>

          <div className={styles.grid__item}>
            <Amenities
              translations={pageTranslations.amenities}
              property={property}
            />
          </div>

          <div className={styles.grid__item}>
            <Availability
              title={pageTranslations.availability.title[router.locale]}
              note={pageTranslations.availability.note[router.locale]}
              translations={pageTranslations}
              cardTranslation={pageTranslations.welcome.card}
              property={property}
              blockedDates={blockedDates}
              minNights={minNights}
              variant={[
                property?.address?.lat && property?.address?.lng && 'pb0'
              ]}
            />
          </div>
        </div>

        {property?.address?.lat && property?.address?.lng && (
          <Map
            variant={['section']}
            lat={property?.address?.lat}
            lng={property?.address?.lng}
          />
        )}

        <Reviews
          translations={pageTranslations.reviews}
          reviews={property.reviews}
          property={property}
        />

        {property?.service && (
          <Bonuses
            title={pageTranslations.bonuses.title[router.locale]}
            label={pageTranslations.bonuses.label[router.locale]}
            services={property?.service}
          />
        )}

        <Contact
          translations={contactTranslation}
          buying={false}
          leftLabel
          disabled={!property.is_active}
          staff={property?.person}
        />

        <Rentals
          title={pageTranslations.rentals.title[router.locale]}
          buttonLabel={pageTranslations.rentals.button[router.locale]}
          properties={recommendedProperties}
          cardTranslation={headerTranslation.card}
        />
      </Page>
    )
  }

  return (
    <FourOhFour
      headerTranslation={headerTranslation}
      footerTranslation={footerTranslation}
    />
  )
}

export async function getStaticPaths() {
  const data = await axios
    .get('listings/ids')
    .then((res) => res.data)
    .catch((error) => console.error(error))

  const paths =
    data &&
    data.map((post) => ({
      params: {
        _name: `${post.nickname}-${post.bedrooms}-bedroom-${post.propertyType}`
      }
    }))

  return {
    paths,
    fallback: true
  }
}

export async function getStaticProps({ params, locale }) {
  const { _name } = params

  moment.locale(locale)

  // Fetch property data
  const propertyResponse = await axios.get(`listings/show/${_name}`)
  const data = propertyResponse.data[locale]

  // Get calendar data right away if we have property data
  let blockedDatesData = []
  if (data?.resource_id) {
    try {
      const startDate = moment().subtract(2, 'day').format('YYYY-MM-DD')
      const endDate = moment().add(6, 'months').format('YYYY-MM-DD')

      const calendarResponse = await axios.get('calendar/dates', {
        params: {
          listingId: data.resource_id,
          startDate,
          endDate
        }
      })

      if (!isEmpty(calendarResponse.data)) {
        blockedDatesData = Object.values(calendarResponse.data).map(
          ({ date }) => moment(date)
        )
      }
    } catch (error) {
      console.error('Failed to fetch calendar data:', error)
      // Continue without calendar data if there's an error
    }
  }

  const initialMinNight = await axios
    // eslint-disable-next-line no-undef
    .get(process.env.NEXT_PUBLIC_API_URL + 'listings/min-nights', {
      params: {
        listingId: data?.resource_id,
        startDate: moment().format('YYYY-MM-DD')
      }
    })
    .then((res) => res.data)

  // Fetch recommended properties in parallel with translations
  const recommendedPropertiesPromise = axios
    .get('listings/similar', {
      params: {
        bedrooms: data?.bedrooms,
        propertyId: data?.resource_id || data?.property_id,
        neighbourhood: data?.neighbourhood,
        price: data?.property_price || data?.price?.usd
      }
    })
    .then((res) => res.data[locale])
    .catch(() => false)

  // Handle translations with caching, preserving your existing approach
  let headerDataTranslation = cache.get('headerTranslations')
  if (!headerDataTranslation) {
    headerDataTranslation = await headerTranslation()
    cache.put('headerTranslations', headerDataTranslation)
  }

  let footerDataTranslation = cache.get('footerTranslations')
  if (!footerDataTranslation) {
    footerDataTranslation = await footerTranslation()
    cache.put('footerTranslations', footerDataTranslation)
  }

  let contactFormTranslationData = cache.get('contactFormTranslations')
  if (!contactFormTranslationData) {
    contactFormTranslationData = await contactTranslation()
    cache.put('contactFormTranslations', contactFormTranslationData) // Fixed: was missing the second parameter
  }

  let ctaDataTranslation = cache.get('ctaTranslations')
  if (!ctaDataTranslation) {
    ctaDataTranslation = await ctaTranslation()
    cache.put('ctaTranslations', ctaDataTranslation)
  }

  // Wait for any async operations that are still pending
  const [recommendedProperties, translatedPageData] = await Promise.all([
    recommendedPropertiesPromise,
    singlePropertyTranslations()
  ])

  let bedrooms = []
  if (data) {
    if (data.property_bedrooms && !isEmpty(data.property_bedrooms)) {
      bedrooms = data.property_bedrooms.map((bedroom) => ({
        value: bedroom.title,
        title: bedroom.description,
        subtitle:
          !isEmpty(bedroom.amenities) &&
          `${translatedPageData.amenities.room[locale]} ${iterateArray(
            bedroom.amenities,
            translatedPageData.amenities.and[locale]
          )}`
      }))
    } else if (data.bedroom_option) {
      bedrooms = data.bedroom_option.map((bedroom) => ({
        value: bedroom?.sleeping_arrangements_title || bedroom?.room_title,
        title: bedroom.description,
        subtitle:
          !isEmpty(bedroom.room_amenities) &&
          `${translatedPageData.amenities.room[locale]} ${iterateArray(
            bedroom.room_amenities,
            translatedPageData.amenities.and[locale]
          )}`
      }))
    }
  }

  // Calculate bathrooms data on the server side
  const bathrooms = data
    ? [
        {
          value: translatedPageData?.amenities.bathrooms[locale],
          title:
            data?.bathroom_description ||
            `${data?.bathrooms} ${translatedPageData?.amenities.bathrooms[locale]}`
        }
      ]
    : []

  // Return all the data as props
  return {
    props: {
      property: data,
      filteredDates: blockedDatesData.map((date) => date.format('YYYY-MM-DD')),
      notFound: false,
      pageTranslations: translatedPageData,
      headerTranslation: headerDataTranslation,
      footerTranslation: footerDataTranslation,
      contactTranslation: contactFormTranslationData,
      ctaTranslation: ctaDataTranslation,
      recommendedProperties: recommendedProperties || false,
      calendarPreloaded: true,
      initialMinNight,
      bedrooms,
      bathrooms
    },
    revalidate: 300
  }
}

export default Property
